import { useCallback } from 'react'
import PropTypes from 'prop-types'
import { Box, FormControl, FormHelperText, FormLabel, Grid, GridItem, Heading, Input } from '@chakra-ui/react'
import { UiSelect } from 'components/common'
import { getInstallationOptions, getShippingOptions, getSiteSurveyOptions } from 'api'
import { EMPTY_UTS_FIELDS, getProfessionalInstallationPermitFee, isProfessionalInstallationOption, isUtsEligibleShipping } from 'lib'

const clearUtsFields = (draft) => {
  Object.assign(draft, EMPTY_UTS_FIELDS)
}

export const EdgeInstallationShippingForm = ({ form, setForm, ...rest }) => {
  const loadInstallationOptions = useCallback((page) => getInstallationOptions(page, 20, '', 'cost', 'asc'), [])
  const loadSiteSurveyOptions = useCallback((page) => getSiteSurveyOptions(page, 20, '', 'cost', 'asc'), [])
  const loadShippingOptions = useCallback((page) => getShippingOptions(page, 20, '', 'cost', 'asc'), [])

  const hasUtsSelection = Boolean(form.utsCarrierName)
  const isProfessionalInstallation = isProfessionalInstallationOption(form.installationOption)
  const permitFee = getProfessionalInstallationPermitFee(form.installationOption)

  return (
    <Box {...rest}>
      <Grid
        templateColumns="5fr 1fr"
        rowGap={5}
        columnGap={10}
        w="900px"
        maxW="100%"
      >
        <GridItem>
          <Heading size="md">Installation & Shipping</Heading>
        </GridItem>
        <GridItem></GridItem>
        <GridItem>
          <FormControl isRequired>
            <FormLabel>Installation</FormLabel>
            <UiSelect
              value={form.installationOption}
              onChange={v => {
                setForm(draft => {
                  draft.installationOption = v
                })
              }}
              loadData={loadInstallationOptions}
              apiRespProp="rows"
              getOptionValue={v => v.id}
              getOptionLabel={v => `${v.description} - $${v.cost}`}
              placeholder="Search"
              noOptionsMessage={() => 'No Installation Options Found'}
              isSearchable={false}
              required={true}
            />
          </FormControl>
        </GridItem>
        <GridItem display="flex" alignItems="flex-end">
          <FormControl>
            <FormLabel>Cost</FormLabel>
            <Input
              type="number"
              value={form.installationCost}
              onChange={e => {
                setForm(draft => {
                  draft.installationCost = e.target.value
                })
              }}
              onBlur={e => {
                setForm(draft => {
                  draft.installationCost = parseFloat(e.target.value || 0).toFixed(2)
                })
              }}
            />
          </FormControl>
        </GridItem>
        {isProfessionalInstallation && (
          <>
            <GridItem>
              <FormControl>
                <FormLabel>Permit</FormLabel>
                <FormHelperText mt={0}>
                  Hard-coded for Professional installation. Added to the total, not included in the installation amount.
                </FormHelperText>
              </FormControl>
            </GridItem>
            <GridItem display="flex" alignItems="flex-end">
              <FormControl>
                <FormLabel>Cost</FormLabel>
                <Input type="number" value={permitFee.toFixed(2)} readOnly />
              </FormControl>
            </GridItem>
          </>
        )}
        <GridItem>
          <FormControl isRequired>
            <FormLabel>Site Survey</FormLabel>
            <UiSelect
              value={form.siteSurveyOption}
              onChange={v => {
                setForm(draft => {
                  draft.siteSurveyOption = v
                  draft.siteSurveyCost = parseFloat((v?.cost || 0).toFixed(2))
                })
              }}
              loadData={loadSiteSurveyOptions}
              apiRespProp="rows"
              getOptionValue={v => v.id}
              getOptionLabel={v => `${v.description} - $${v.cost}`}
              placeholder="Search"
              noOptionsMessage={() => 'No Site Survey Options Found'}
              isSearchable={false}
              required={true}
            />
          </FormControl>
        </GridItem>
        <GridItem display="flex" alignItems="flex-end">
          <FormControl>
            <FormLabel>Cost</FormLabel>
            <Input
              value={form.siteSurveyCost}
              onChange={e => {
                setForm(draft => {
                  draft.siteSurveyCost = e.target.value
                })
              }}
              onBlur={e => {
                setForm(draft => {
                  draft.siteSurveyCost = parseFloat(e.target.value || 0).toFixed(2)
                })
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl isRequired>
            <FormLabel>Shipping</FormLabel>
            <UiSelect
              value={form.shippingOption}
              onChange={v => {
                setForm(draft => {
                  draft.shippingOption = v
                  draft.shippingCost = parseFloat((v?.cost || 0).toFixed(2))
                  if (!isUtsEligibleShipping(v)) {
                    clearUtsFields(draft)
                  }
                })
              }}
              loadData={loadShippingOptions}
              apiRespProp="rows"
              getOptionValue={v => v.id}
              getOptionLabel={v => `${v.description} - $${v.cost}`}
              placeholder="Search"
              noOptionsMessage={() => 'No Shipping Options Found'}
              isSearchable={false}
              required={true}
            />
          </FormControl>
        </GridItem>
        <GridItem display="flex" alignItems="flex-end">
          <FormControl>
            <FormLabel>Cost</FormLabel>
            <Input
              type="number"
              value={form.shippingCost}
              onChange={e => {
                setForm(draft => {
                  draft.shippingCost = e.target.value
                  if (hasUtsSelection) {
                    clearUtsFields(draft)
                  }
                })
              }}
              onBlur={e => {
                setForm(draft => {
                  draft.shippingCost = parseFloat(e.target.value || 0).toFixed(2)
                })
              }}
            />
            {hasUtsSelection && (
              <FormHelperText fontSize="xs" color="blue.600">
                From UTS: {form.utsCarrierName}
                {form.utsServiceLevel ? ` (${form.utsServiceLevel})` : ''}
                {form.utsEstimateNumber ? ` — Estimate #${form.utsEstimateNumber}` : ''}
              </FormHelperText>
            )}
          </FormControl>
        </GridItem>
      </Grid>
    </Box>
  )
}

EdgeInstallationShippingForm.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func
}
